import { gql } from "urql";
import type { TypedDocumentNode } from "urql";
import { useLocale } from "@acto/web-common/locales";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { formatDayMonthYear, formatCurrency } from "@acto/taskpane/field-sales-2/utils/format";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";

const PAGE_SIZE = 10;

interface OfferNode {
  id: string;
  validFrom: string | null;
  value: number | null;
  externalReference: string;
}

interface OffersConnectionTotals {
  value: number | null;
}

interface OffersQueryData {
  clients: {
    edges: {
      node: {
        id: string;
        offers: {
          edges: { cursor: string; node: OfferNode }[];
          pageInfo: { hasNextPage: boolean; endCursor: string | null };
          totals: OffersConnectionTotals | null;
        } | null;
      };
    }[];
  } | null;
}

interface OffersVariables {
  clientId: string;
  first: number;
  after?: string | null;
  dateRange?: { startDate: string; endDate: string };
}

const ClientOffersQuery: TypedDocumentNode<OffersQueryData, OffersVariables> = gql`
  query GetClientOffers($clientId: ID!, $first: Int, $after: String, $dateRange: DateRangeInput) {
    clients(first: 1, filter: { id: $clientId }) {
      edges {
        node {
          id
          offers(first: $first, after: $after, dateRange: $dateRange) {
            edges {
              cursor
              node {
                id
                validFrom
                value
                externalReference
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
            totals {
              value
            }
          }
        }
      }
    }
  }
`;

export interface OfferItem {
  id: string;
  externalReference: string;
  validFrom: string | null;
  formattedDate: string;
  value: number | null;
  formattedValue: string;
}

export interface ClientOffers {
  isPending: boolean;
  isEmpty: boolean;
  error: Error | undefined;
  items: OfferItem[];
  totalValue: number;
  formattedTotalValue: string;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
}

export function useClientOffers(
  clientId: string | undefined,
  dateRange?: { startDate: string; endDate: string } | null,
): ClientOffers {
  const { locale } = useLocale();
  const currency = useCurrencyContext();

  const rawOffers = usePagination<OffersQueryData, OffersVariables, OfferNode, OffersConnectionTotals>({
    query: ClientOffersQuery,
    limit: PAGE_SIZE,
    variables: { clientId: clientId ?? "", dateRange: dateRange ?? undefined },
    enabled: !!clientId,
    connection: (data) =>
      data.clients?.edges?.[0]?.node?.offers ?? {
        edges: [],
        pageInfo: { hasNextPage: false, endCursor: null },
      },
    metadata: (data) => data.clients?.edges?.[0]?.node?.offers?.totals ?? undefined,
  });

  const { data: offers, hasNextPage, fetchNextPage, isFetching, error, metadata } = rawOffers;

  const items = (offers ?? []).map((offer) => ({
    id: offer.id,
    externalReference: offer.externalReference,
    validFrom: offer.validFrom,
    formattedDate: offer.validFrom != null ? formatDayMonthYear(offer.validFrom, locale) : "—",
    value: offer.value ?? null,
    formattedValue: offer.value != null ? formatCurrency(offer.value, currency, locale) : "—",
  }));

  const totalValue = metadata?.value ?? 0;

  const formattedTotalValue = formatCurrency(totalValue, currency, locale);

  const isFirstPageLoading = isFetching && items.length === 0;

  return {
    isPending: isFirstPageLoading,
    isEmpty: !isFirstPageLoading && !error && items.length === 0,
    error,
    items,
    totalValue,
    formattedTotalValue,
    hasMore: hasNextPage,
    isLoadingMore: isFetching && items.length > 0,
    loadMore: fetchNextPage,
  };
}
